import {
    invoicingLabelOf,
    invoicingStatusOf,
    READINESS_REASON_PHRASES,
    reasonsSubline,
    type InvoicingStatus,
    type TmsInvoiceRow,
    type TmsInvoiceWorklist,
} from '@numeo/types-main-web-app'

/** The filter buttons above the table. `prepared` is the batch and emailed states; `ready` also takes the drafts. */
export type TableFilter = 'all' | 'missing_pod' | 'needs_review' | 'ready' | 'prepared'

export const TABLE_FILTERS: ReadonlyArray<{ key: TableFilter; label: string }> = [
    { key: 'all', label: 'All loads' },
    { key: 'missing_pod', label: 'Missing POD' },
    { key: 'needs_review', label: 'Needs review' },
    { key: 'ready', label: 'Ready' },
    { key: 'prepared', label: 'Prepared' },
]

const FILTER_STATUSES: Record<Exclude<TableFilter, 'all'>, InvoicingStatus[]> = {
    missing_pod: ['missing_pod'],
    needs_review: ['needs_review'],
    ready: ['ready', 'draft'],
    prepared: ['batch_prepared', 'sent'],
}

/**
 * Whether the user asked for the ready list (v17 `isReadyListRequest`): "ready" with loads or invoices, and not a request to submit,
 * prepare or create. Only then does the table open on Ready; "Check delivered loads" and "What needs my attention?" open on All loads.
 */
export const isReadyListRequest = (text: string | undefined): boolean =>
    !!text && /ready/i.test(text) && /load|invoice/i.test(text) && !/submit|prepare|create/i.test(text)

/** Whether a row in this status is listed under the filter. `All` lists every row, the `other` ones included. */
export const filterMatches = (filter: TableFilter, status: InvoicingStatus): boolean => filter === 'all' || FILTER_STATUSES[filter].includes(status)

/** The order the table lists rows in: Missing POD, Needs review, Ready, Prepared, then the rest. */
const ORDER: Record<InvoicingStatus, number> = { missing_pod: 0, needs_review: 1, ready: 2, draft: 2, batch_prepared: 3, sent: 3, other: 4 }

/** The one key a row is selected and read by: its load number (the readiness store is keyed the same way). */
export const rowKeyOf = (row: Pick<TmsInvoiceRow, 'load_numbers' | 'load_id' | 'invoice_id'>): string =>
    row.load_numbers?.[0] ?? row.load_id ?? row.invoice_id ?? ''

/** The status of a row, the one rule `@numeo/types-main-web-app` owns. */
export const statusOf = (row: TmsInvoiceRow): InvoicingStatus => invoicingStatusOf(row)

/**
 * A delivered load whose documents and rate are in order but which the Invoices page does not show as Ready (`not_marked_ready`): the
 * only reason it carries. v17 has no word for it, so it reads as Needs attention, and its sub-line says where it stands and what to do.
 */
const notMarkedReadyOf = (row: TmsInvoiceRow) => {
    const reasons = row.reasons ?? []
    return reasons.length > 0 && reasons.every((reason) => reason.code === 'not_marked_ready') ? reasons[0] : undefined
}

const AR_STATUS_WORDS: Record<string, string> = { draft: 'Draft', issue: 'Issue', invoiced: 'Invoiced', closed: 'Closed' }

/** The sub-line of a load the Invoices page does not show as Ready, with what clears it. */
const notMarkedReadySubline = (arStatus: string | undefined): string =>
    `${arStatus && AR_STATUS_WORDS[arStatus] ? `${AR_STATUS_WORDS[arStatus]} on the Invoices page, not Ready` : 'Not Ready on the Invoices page'} · Mark it Ready there to invoice`

/** The badge's words. `other` shows the row's first reason, not a status (spec §4); a reason v17 has no word for reads Needs attention. */
export const badgeLabelOf = (row: TmsInvoiceRow, status: InvoicingStatus = statusOf(row)): string => {
    if (status !== 'other' || notMarkedReadyOf(row)) return invoicingLabelOf(status)
    const first = row.reasons?.[0]?.code
    return (first && READINESS_REASON_PHRASES[first]) || invoicingLabelOf(status)
}

/**
 * The row's sub-line under its status: what is still missing, or the one fact that makes it ready or prepared. A prepared row names its
 * batch and the factor it is not sent to yet (v17: "In BATCH-MW0JSC4S · Not sent to RTS Pro").
 */
export const sublineOf = (row: TmsInvoiceRow, status: InvoicingStatus = statusOf(row), provider?: string): string => {
    switch (status) {
        case 'sent':
            return row.invoice_number ? `Invoice ${row.invoice_number} emailed` : 'Invoice emailed'
        case 'batch_prepared':
            // With no factor connected the batch is only a group on the Invoices page: nothing is waiting on a factor.
            if (!provider) return `${row.batch_name ? `In ${row.batch_name}` : 'In a batch'} · Not sent yet`
            return `${row.batch_name ? `In ${row.batch_name}` : 'Prepared for the factor'} · Not sent to ${provider}`
        case 'draft':
            return 'Invoice created · Not sent yet'
        case 'ready':
            return reasonsSubline(row) || 'Rate con + POD matched'
        case 'missing_pod':
            return reasonsSubline(row) || 'Signed POD missing'
        case 'needs_review':
            return reasonsSubline(row) || 'POD needs verification'
        case 'other': {
            const notMarked = notMarkedReadyOf(row)
            if (notMarked) return notMarkedReadySubline(notMarked.ar_status)
            return reasonsSubline(row) || 'Needs attention'
        }
    }
}

/** A row's amount: the invoice's total once it has one (the card's `amount_due_usd`), else the load's line haul. */
export const amountOf = (row: TmsInvoiceRow): number => row.amount_due_usd ?? 0

/**
 * The worklist's rows, ready ones first, each load once. A windowed read's invoiced loads (created and not sent, batched, sent) follow,
 * so a week's table lists every load delivered in it, its draft as "Ready to send · Invoice created".
 */
export const worklistRowsOf = (worklist: TmsInvoiceWorklist): TmsInvoiceRow[] => {
    const seen = new Set<string>()
    return [...worklist.ready.rows, ...worklist.needs_documents.rows, ...(worklist.invoiced?.rows ?? [])].filter((row) => {
        const key = rowKeyOf(row)
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

/** The rows as the table shows them: each row replaced by its newer read when the store holds one (a write updates it in place). */
export const withStoreRows = (rows: TmsInvoiceRow[], stored: Record<string, TmsInvoiceRow>): TmsInvoiceRow[] => rows.map((row) => stored[rowKeyOf(row)] ?? row)

/** The rows sorted by status order, stable within a status. */
export const sortedByStatus = (rows: TmsInvoiceRow[]): TmsInvoiceRow[] => [...rows].sort((a, b) => ORDER[statusOf(a)] - ORDER[statusOf(b)])

/** The reason a row cannot be sent (an email or a factoring batch), or null when it can. Shared by both drawers and the chat card. */
export const invoiceBlockOf = (row: TmsInvoiceRow, status: InvoicingStatus = statusOf(row)): string | null => {
    switch (status) {
        case 'ready':
        case 'draft':
            return null
        case 'sent':
            return 'Already sent'
        case 'batch_prepared':
            return 'In a factoring batch'
        case 'missing_pod':
            return 'Missing POD'
        case 'needs_review':
            return 'Needs review'
        case 'other':
            return badgeLabelOf(row, status)
    }
}

/**
 * The reason a row cannot be emailed, or null when it can. An invoice in a batch can be: the Invoices page emails a batch too. Only a
 * factoring batch refuses it (`invoiceBlockOf`), so an invoice is never grouped twice.
 */
export const emailBlockOf = (row: TmsInvoiceRow, status: InvoicingStatus = statusOf(row)): string | null =>
    status === 'batch_prepared' ? null : invoiceBlockOf(row, status)

/** The rows that can be sent or batched, in the order given. */
export const eligibleOf = (rows: TmsInvoiceRow[]): TmsInvoiceRow[] => rows.filter((row) => invoiceBlockOf(row) === null)
